/**
 * S012 authenticated chat-list page, S013 empty-state page, and the
 * bounded successor-route placeholder.
 *
 * Figma authority:
 * - S012 loaded list:  R4VYIdXEv7HYIXtpDMEvHY:38:2717
 * - S013 empty state:  R4VYIdXEv7HYIXtpDMEvHY:38:2884
 *
 * The page exposes a `createPort` dependency-injection seam so component
 * tests can deterministically render the empty state without changing
 * the production fixture. The production `/chat` route continues to
 * render `<ChatListPage />` with no props.
 *
 * A narrowly gated E2E fixture signal (see
 * `features/chat-list/e2eFixtureSignal.ts`) overrides the production
 * default in Playwright runs only. Production builds with
 * `import.meta.env.MODE !== 'e2e'` never read the window global.
 */
import { useEffect, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  createEmptyFixtureChatListPort,
  createFixtureChatListPort,
  FIXTURE_REFERENCE_TIME,
} from '@/features/chat-list/adapters/fixtureChatListPort';
import { readE2EChatListFixtureSelection } from '@/features/chat-list/e2eFixtureSignal';
import type { ChatListPort } from '@/features/chat-list/port';
import { useChatListProgram } from '@/features/chat-list/useChatListProgram';
import { ChatListView } from '@/features/chat-list/view';
import { useChatProgram } from '@/features/chat/useChatProgram';
import { ChatView } from '@/features/chat/view';
import { RoutePaths } from '@/routes';

export interface ChatListPageProps {
  /**
   * Optional port factory used by component tests to substitute the
   * production fixture. When omitted, the page uses the canonical loaded
   * S012 fixture. The E2E fixture signal is only consulted when
   * `import.meta.env.MODE === 'e2e'` and overrides this argument.
   */
  readonly createPort?: () => ChatListPort;
}

export function ChatListPage({
  createPort,
}: ChatListPageProps = {}) {
  const port = useMemo(() => {
    const selection = readE2EChatListFixtureSelection();
    if (selection.resolvedEmpty) {
      return createEmptyFixtureChatListPort();
    }
    const factory = createPort ?? createFixtureChatListPort;
    return factory();
  }, [createPort]);
  const nextLoadId = useRef(0);
  const { model, start } = useChatListProgram({ port });

  useEffect(() => {
    nextLoadId.current += 1;
    start(nextLoadId.current);
  }, [start]);

  return (
    <ChatListView
      model={model}
      referenceTime={FIXTURE_REFERENCE_TIME}
    />
  );
}

/**
 * S014 owns the individual-conversation UI. This page is route-composition
 * glue around the existing `useChatProgram` hook — it does NOT introduce a
 * second state owner. The program is the single source of truth for the
 * channel list and the open channel.
 *
 * On mount / route change:
 *   - `OpenChannel(channelId)` is dispatched with the route param, which
 *     flips the program into `viewMode: 'channel-detail'` and triggers a
 *     `LoadMessages` effect for that channel.
 *   - A `lastOpenedRef` guard prevents re-opening when only the route param
 *     reference changes, so SPA navigation between channels works without
 *     a hard reload.
 *
 * IMPORTANT: this page does NOT dispatch `Started`. The channel-list load
 * is owned by `ChatListPage` (the `/chat` route). Dispatching `Started`
 * here would replace the `LoadMessages` effect that `OpenChannel` just
 * queued, because `useChatProgram`'s reducer replaces `pendingEffects`
 * on every dispatch (it does NOT accumulate). This was a latent defect
 * fixed by ACT-INDEEP-SPONSOR-FRIEND-PERSONAL-CHAT-SYNC-VERTICAL01 and
 * re-fixed by ACT-INDEEP-REMOTE-MAIN-RECONCILIATION01 corrective commit.
 *
 * Backend is authoritative for authorization (CHAT_READY), membership,
 * and sender identity. The frontend merely projects what the server
 * returned; client-side gating is convenience only. The error branch
 * below is realtime-aware UI only.
 */
export function ChatChannelPage() {
  const { channelId } = useParams<{ channelId: string }>();
  const program = useChatProgram();
  const lastOpenedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!channelId || lastOpenedRef.current === channelId) {
      return;
    }
    lastOpenedRef.current = channelId;
    program.openChannel(channelId);
  }, [channelId, program]);

  if (program.model.status === 'error') {
    return (
      <section className="chat-channel-error" data-testid="chat-channel-error">
        <h1>Chat</h1>
        <p>{program.model.error ?? 'Could not load this chat.'}</p>
        <button onClick={program.retry} data-testid="chat-channel-retry">Retry</button>
        <Link to={RoutePaths.CHAT}>Back to chats</Link>
      </section>
    );
  }

  return (
    <ChatView
      model={program.model}
      onStart={program.start}
      onOpenChannel={program.openChannel}
      onBackToList={program.backToList}
      onShowCreateForm={program.showCreateForm}
      onHideCreateForm={program.hideCreateForm}
      onCreateChannel={program.createChannel}
      onSendMessage={program.sendMessage}
      onNewMessageChanged={program.newMessageChanged}
      onRetry={program.retry}
    />
  );
}

/** Compatibility export for existing callers while `/chat` is S012-owned. */
export const ChatPage = ChatListPage;
